import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight, Calendar } from "lucide-react";
import { blogPosts } from "@/data/blogPosts";
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationLink,
  PaginationPrevious,
  PaginationNext
} from "@/components/ui/pagination";
import { Helmet } from "react-helmet-async";

const POSTS_PER_PAGE = 6; // Show 6 posts per page

const Blog = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const currentPage = parseInt(queryParams.get("page") || "1", 10);
  
  // Sort blog posts by date in descending order (newest first)
  const sortedBlogPosts = [...blogPosts].sort((a, b) => {
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });
  
  // Calculate the total number of pages
  const totalPages = Math.ceil(sortedBlogPosts.length / POSTS_PER_PAGE);
  
  // Get the posts for the current page
  const indexOfLastPost = currentPage * POSTS_PER_PAGE;
  const indexOfFirstPost = indexOfLastPost - POSTS_PER_PAGE;
  const currentPosts = sortedBlogPosts.slice(indexOfFirstPost, indexOfLastPost);
  
  // Redirect invalid or redundant page params
  useEffect(() => {
    if (currentPage === 1 && location.search.includes("page=")) {
      navigate("/blog", { replace: true });
      return;
    }
    if (currentPage < 1 || currentPage > totalPages) {
      navigate("/blog?page=1", { replace: true });
    }
  }, [currentPage, navigate, totalPages, location.search]);
  
  // Handle page change
  const handlePageChange = (page: number) => {
    navigate(`/blog?page=${page}`);
    window.scrollTo(0, 0);
  };

  const pageTitle =
    currentPage > 1
      ? `YouAux Blog | Page ${currentPage}`
      : "YouAux Blog | Music Distribution Platform";

  const canonical =
    currentPage > 1
      ? `https://youaux.com/blog?page=${currentPage}`
      : "https://youaux.com/blog";

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content="Tips, guides, and industry insights to help you navigate your music career." />
        <link rel="canonical" href={canonical} />
      </Helmet>
      <Navbar />
      <main className="pt-20">
        {/* Hero Section */}
        <div className="w-full bg-ophira-dark pt-16 relative overflow-hidden">
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="ophira-section-title mb-6">YouAux Blog</h1>
              <p className="ophira-subtitle text-xl mb-8">
                Tips, guides, and industry insights to help you navigate your music career.
              </p>
              <div className="flex items-center justify-center">
                <span className="text-ophira-yellow font-semibold">
                  {sortedBlogPosts.length} Articles Published
                </span>
              </div>
            </div>
          </div>
          <div className="absolute inset-0 bg-noise-pattern opacity-5 pointer-events-none"></div>
          <div className="absolute inset-0 bg-grid-pattern opacity-5 pointer-events-none"></div>
        </div>

        {/* Blog Grid */}
        <div className="bg-ophira-dark py-20">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {currentPosts.map((post) => (
                <Link to={`/blog/${post.slug}`} key={post.id} className="group">
                  <div className="bg-ophira-darkgray/40 h-full rounded-2xl overflow-hidden">
                    <div className="overflow-hidden bg-black">
                      <img
                        src={post.image}
                        alt={post.title}
                        className={`w-full h-64 ${post.id === 12 ? 'object-contain' : 'object-cover'} bg-[#121212] transition-transform duration-700 group-hover:scale-110`}
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center mb-4 text-ophira-yellow">
                        <Calendar className="h-4 w-4 mr-2" />
                        <span className="text-sm">{post.date}</span>
                      </div>
                      <h3 className="text-xl font-bold text-white mb-4 group-hover:text-ophira-yellow transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-ophira-lightgray mb-6">
                        {post.excerpt}
                      </p>
                      <div className="ophira-more-link">
                        Read Full Article: {post.title} <ArrowRight className="ml-2 h-4 w-4" />
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-16">
                <Pagination>
                  <PaginationContent>
                    {currentPage > 1 && (
                      <PaginationItem>
                        <PaginationPrevious 
                          onClick={() => handlePageChange(currentPage - 1)} 
                          className="bg-ophira-darkgray/40 cursor-pointer"
                        />
                      </PaginationItem>
                    )}
                    
                    {Array.from({ length: totalPages }).map((_, index) => (
                      <PaginationItem key={index}>
                        <PaginationLink 
                          isActive={currentPage === index + 1}
                          onClick={() => handlePageChange(index + 1)}
                          className={`bg-ophira-darkgray/40 cursor-pointer ${
                            currentPage === index + 1 ? "text-ophira-yellow border-ophira-yellow" : ""
                          }`}
                        >
                          {index + 1}
                        </PaginationLink>
                      </PaginationItem>
                    ))}
                    
                    {currentPage < totalPages && (
                      <PaginationItem>
                        <PaginationNext 
                          onClick={() => handlePageChange(currentPage + 1)} 
                          className="bg-ophira-darkgray/40 cursor-pointer"
                        />
                      </PaginationItem>
                    )}
                  </PaginationContent>
                </Pagination>
              </div>
            )}
            
            {/* CTA Section */}
            <div className="mt-20 border border-ophira-darkgray/50 p-10 bg-ophira-darkgray/20 rounded-2xl">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div>
                  <h3 className="text-2xl font-bold mb-2">Want to learn more about music distribution?</h3>
                  <p className="text-ophira-lightgray">
                    Subscribe to our newsletter to receive the latest tips and industry news.
                  </p>
                </div>
                <Link to="/contact">
                  <Button className="ophira-button whitespace-nowrap rounded-2xl">Contact Us</Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default Blog;
